import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { StatCard } from '../components/StatCard'
import { Badge } from '../components/Badge'
import { DollarSign, ShoppingBag, TrendingUp, Loader2, FileSpreadsheet, FileText, Filter, Truck, Package, ChevronRight, ChevronDown } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

interface ReportesData {
  topProductos: Array<{ nombre: string; vendidos: number; ingresos: number }>
  porFormaPago: Array<{ fp: string; cantidad: number; total: number }>
  porMes: Array<{ mes: string; pedidos: number; ingresos: number }>
}

interface EnvioGuia {
  id: number
  carrier: string
  service: string
  cobradoCents: number
  costoRealCents: number | null
  trackingNumber: string
  weightKg: number | null
  dims: string | null
}

interface EnvioPedido {
  orderId: number
  orderNumber: string
  customerName: string
  createdAt: string
  nGuias: number
  costoRealTotal: number
  cobradoTotal: number
  guias: EnvioGuia[]
}

interface EnviosData {
  data: EnvioPedido[]
  totales: { pedidos: number; guias: number; costoRealCents: number; cobradoCents: number }
}

/** Formatea centavos → moneda MXN con 2 decimales (los montos de envío traen centavos). */
function centsToMXN(cents: number | null | undefined): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents ?? 0) / 100)
}

const MESES_LARGOS: Record<string, string> = {
  '01': 'Ene',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Abr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Ago',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dic',
}

function formatMes(mes: string) {
  const [yyyy, mm] = mes.split('-')
  return `${(mm && MESES_LARGOS[mm]) || mm || ''} ${yyyy?.slice(2) || ''}`
}

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'paid', label: 'Pagados' },
  { value: 'preparing', label: 'Preparando' },
  { value: 'shipped', label: 'Enviados' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'refunded', label: 'Reembolsados' },
]

function buildQS(params: Record<string, string>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v) qs.append(k, v)
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/**
 * Descarga un archivo navegando a la URL — la cookie httpOnly de sesión
 * se envía automáticamente. window.open con _self mantiene la barra de
 * progreso del navegador y respeta el Content-Disposition: attachment.
 */
function downloadFile(url: string) {
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export function Reportes() {
  const { data, isLoading, error } = useQuery<ReportesData>({
    queryKey: ['reportes'],
    queryFn: () => apiGet('/api/admin/reportes'),
  })

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')

  // Envíos / Guías por pedido
  const enviosQS = buildQS({ from, to, limit: '300' })
  const {
    data: envios,
    isLoading: enviosLoading,
    error: enviosError,
  } = useQuery<EnviosData>({
    queryKey: ['reportes-envios', from, to],
    queryFn: () => apiGet(`/api/admin/reportes/envios${enviosQS}`),
  })

  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  function toggleRow(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function downloadXlsx() {
    const qs = buildQS({ from, to, status, search })
    downloadFile(`/api/admin/reportes/ventas/export${qs}`)
  }
  function downloadCsv() {
    // Para guías el default es status=preparing si no se especifica
    const qs = buildQS({ from, to, status: status || 'preparing', search })
    downloadFile(`/api/admin/reportes/guias/export${qs}`)
  }

  // Cálculos derivados
  const ventasTotales = data
    ? data.porMes.reduce((acc, m) => acc + m.ingresos, 0)
    : 0
  const pedidosMes = data && data.porMes.length > 0 ? (data.porMes[data.porMes.length - 1]?.pedidos ?? 0) : 0
  const pedidosTotales = data ? data.porMes.reduce((acc, m) => acc + m.pedidos, 0) : 0
  const ticketPromedio = pedidosTotales > 0 ? ventasTotales / pedidosTotales : 0

  const maxIngresoMes = data
    ? Math.max(1, ...data.porMes.map((m) => m.ingresos))
    : 1

  const totalFP = data ? data.porFormaPago.reduce((acc, f) => acc + f.total, 0) : 0

  return (
    <>
      <Topbar
        title="Reportes"
        subtitle="Métricas de ventas y rendimiento"
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={downloadXlsx}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <FileSpreadsheet className="size-3.5" /> Excel ventas
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
            >
              <FileText className="size-3.5" /> CSV guías
            </button>
          </div>
        }
      />

      <div className="p-6">
        {/* Panel de descargas con filtros */}
        <div className="mb-6 rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-900">
            <Filter className="size-4 text-ink-500" />
            Filtros para descargas
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                Desde
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                Hasta
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                Estado
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                Buscar
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nro pedido, cliente, email…"
                className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
          </div>
          <p className="mt-3 text-[11px] text-ink-500">
            💡 <strong>Excel ventas</strong>: reporte contable con costo, IVA, comisión y rentabilidad por línea.
            <strong className="ml-2">CSV guías</strong>: listado para impresión de guías (default: estado "Preparando").
          </p>
        </div>

        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Calculando métricas…</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard
                label="Ventas totales (12 meses)"
                value={formatMXN(ventasTotales)}
                icon={DollarSign}
                color="emerald"
              />
              <StatCard
                label="Pedidos del último mes"
                value={pedidosMes.toLocaleString('es-MX')}
                icon={ShoppingBag}
                color="red"
              />
              <StatCard
                label="Ticket promedio"
                value={formatMXN(ticketPromedio)}
                icon={TrendingUp}
                color="violet"
              />
            </div>

            {/* Gráfico ventas por mes */}
            <div className="mt-6 rounded-2xl border border-ink-200 bg-white p-5">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-ink-900">Ventas por mes</h2>
                  <p className="text-xs text-ink-500">Últimos 12 meses</p>
                </div>
              </div>

              {data.porMes.length === 0 ? (
                <p className="py-10 text-center text-sm text-ink-500">Sin datos.</p>
              ) : (
                <div className="flex items-end gap-2 sm:gap-3" style={{ height: 240 }}>
                  {data.porMes.map((m) => {
                    const h = Math.max(2, (m.ingresos / maxIngresoMes) * 100)
                    return (
                      <div key={m.mes} className="group flex flex-1 flex-col items-center gap-1">
                        <div className="text-[10px] font-semibold text-ink-700 opacity-0 transition-opacity group-hover:opacity-100">
                          {formatMXN(m.ingresos)}
                        </div>
                        <div className="relative flex w-full items-end" style={{ height: 180 }}>
                          <div
                            className="w-full rounded-t-md bg-gradient-to-t from-brand-500 to-brand-400 transition-all hover:from-brand-600 hover:to-brand-500"
                            style={{ height: `${h}%` }}
                          />
                        </div>
                        <div className="text-[10px] font-medium text-ink-500">
                          {formatMes(m.mes)}
                        </div>
                        <div className="text-[9px] text-ink-400">
                          {m.pedidos.toLocaleString('es-MX')} ped.
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Top productos */}
              <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
                <div className="border-b border-ink-200 px-5 py-4">
                  <h2 className="font-semibold text-ink-900">Top productos vendidos</h2>
                  <p className="text-xs text-ink-500">Los 10 más vendidos del histórico</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-5 py-3 font-medium">Producto</th>
                      <th className="px-5 py-3 font-medium text-right">Unidades</th>
                      <th className="px-5 py-3 font-medium text-right">Ingresos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topProductos.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-10 text-center text-sm text-ink-500">
                          Sin datos.
                        </td>
                      </tr>
                    )}
                    {data.topProductos.map((p, i) => (
                      <tr
                        key={`${p.nombre}-${i}`}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-50 text-[10px] font-bold text-brand-700">
                              {i + 1}
                            </span>
                            <span className="line-clamp-1 text-ink-900">{p.nombre}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-ink-900">
                          {p.vendidos.toLocaleString('es-MX')}
                        </td>
                        <td className="px-5 py-3 text-right text-ink-700">
                          {formatMXN(p.ingresos)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Ventas por forma de pago */}
              <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
                <div className="border-b border-ink-200 px-5 py-4">
                  <h2 className="font-semibold text-ink-900">Ventas por forma de pago</h2>
                  <p className="text-xs text-ink-500">Distribución del histórico</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-5 py-3 font-medium">Forma de pago</th>
                      <th className="px-5 py-3 font-medium text-right">Pedidos</th>
                      <th className="px-5 py-3 font-medium text-right">Total</th>
                      <th className="px-5 py-3 font-medium text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.porFormaPago.length === 0 && (
                      <tr>
                        <td colSpan={4} className="py-10 text-center text-sm text-ink-500">
                          Sin datos.
                        </td>
                      </tr>
                    )}
                    {data.porFormaPago.map((f) => {
                      const pct = totalFP > 0 ? (f.total / totalFP) * 100 : 0
                      return (
                        <tr
                          key={f.fp}
                          className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                        >
                          <td className="px-5 py-3">
                            <Badge variant="info">{f.fp}</Badge>
                          </td>
                          <td className="px-5 py-3 text-right text-ink-700">
                            {f.cantidad.toLocaleString('es-MX')}
                          </td>
                          <td className="px-5 py-3 text-right font-semibold text-ink-900">
                            {formatMXN(f.total)}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-ink-100">
                                <div
                                  className="h-full rounded-full bg-brand-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold text-ink-700">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ─── Envíos / Guías por pedido ─── */}
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-2">
            <Truck className="size-5 text-brand-600" />
            <div>
              <h2 className="font-semibold text-ink-900">Envíos / Guías por pedido</h2>
              <p className="text-xs text-ink-500">
                Costo real del transportista vs. tarifa cobrada al cliente
                {(from || to) && ' (según el periodo filtrado arriba)'}
              </p>
            </div>
          </div>

          {enviosLoading && (
            <div className="grid place-items-center py-16 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando envíos…</p>
            </div>
          )}

          {enviosError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {(enviosError as Error).message}
            </div>
          )}

          {envios && (
            <>
              {(() => {
                const diff = envios.totales.cobradoCents - envios.totales.costoRealCents
                const positive = diff >= 0
                return (
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                    <StatCard
                      label="Pedidos con guía"
                      value={envios.totales.pedidos.toLocaleString('es-MX')}
                      icon={ShoppingBag}
                      color="blue"
                    />
                    <StatCard
                      label="Guías totales"
                      value={envios.totales.guias.toLocaleString('es-MX')}
                      icon={Package}
                      color="violet"
                    />
                    <StatCard
                      label="Costo real (transportista)"
                      value={centsToMXN(envios.totales.costoRealCents)}
                      icon={Truck}
                      color="amber"
                    />
                    <StatCard
                      label="Cobrado al cliente"
                      value={centsToMXN(envios.totales.cobradoCents)}
                      icon={DollarSign}
                      color="emerald"
                    />
                    <div
                      className={`rounded-2xl border p-5 ${
                        positive
                          ? 'border-emerald-200 bg-emerald-50'
                          : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className={`text-sm font-medium ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
                          Diferencia (cobrado − real)
                        </div>
                        <div
                          className={`grid size-9 place-items-center rounded-lg ${
                            positive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}
                        >
                          <TrendingUp className="size-5" />
                        </div>
                      </div>
                      <div className={`mt-3 text-2xl font-extrabold tracking-tight ${positive ? 'text-emerald-700' : 'text-red-700'}`}>
                        {positive ? '+' : ''}
                        {centsToMXN(diff)}
                      </div>
                      <div className={`mt-1 text-[11px] font-medium ${positive ? 'text-emerald-600' : 'text-red-600'}`}>
                        {positive ? 'Ganancia en envíos' : 'Pérdida en envíos'}
                      </div>
                    </div>
                  </div>
                )
              })()}

              <div className="mt-6 overflow-hidden rounded-2xl border border-ink-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium text-right"># Guías</th>
                      <th className="px-4 py-3 font-medium text-right">Costo real</th>
                      <th className="px-4 py-3 font-medium text-right">Cobrado</th>
                      <th className="px-4 py-3 font-medium text-right">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envios.data.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-sm text-ink-500">
                          Sin envíos en el periodo.
                        </td>
                      </tr>
                    )}
                    {envios.data.map((p) => {
                      const isOpen = expanded.has(p.orderId)
                      const rowDiff = p.cobradoTotal - p.costoRealTotal
                      const rowPositive = rowDiff >= 0
                      return (
                        <FragmentRow
                          key={p.orderId}
                          p={p}
                          isOpen={isOpen}
                          rowDiff={rowDiff}
                          rowPositive={rowPositive}
                          onToggle={() => toggleRow(p.orderId)}
                        />
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

function FragmentRow({
  p,
  isOpen,
  rowDiff,
  rowPositive,
  onToggle,
}: {
  p: EnvioPedido
  isOpen: boolean
  rowDiff: number
  rowPositive: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr
        className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="size-4 shrink-0 text-ink-500" />
            ) : (
              <ChevronRight className="size-4 shrink-0 text-ink-500" />
            )}
            <span className="font-semibold text-ink-900">{p.orderNumber}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-ink-700">
          <span className="line-clamp-1">{p.customerName}</span>
        </td>
        <td className="px-4 py-3 text-right text-ink-700">
          {p.nGuias.toLocaleString('es-MX')}
        </td>
        <td className="px-4 py-3 text-right text-ink-700">{centsToMXN(p.costoRealTotal)}</td>
        <td className="px-4 py-3 text-right font-semibold text-ink-900">{centsToMXN(p.cobradoTotal)}</td>
        <td className={`px-4 py-3 text-right font-semibold ${rowPositive ? 'text-emerald-600' : 'text-red-600'}`}>
          {rowPositive ? '+' : ''}
          {centsToMXN(rowDiff)}
        </td>
      </tr>
      {isOpen && (
        <tr className="border-t border-ink-100 bg-ink-50/60">
          <td colSpan={6} className="px-4 py-3">
            <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-ink-50">
                  <tr className="text-left uppercase tracking-wider text-ink-500">
                    <th className="px-3 py-2 font-medium">Paquetería</th>
                    <th className="px-3 py-2 font-medium">Servicio</th>
                    <th className="px-3 py-2 font-medium">Tracking</th>
                    <th className="px-3 py-2 font-medium">Medidas / Peso</th>
                    <th className="px-3 py-2 font-medium text-right">Costo real</th>
                    <th className="px-3 py-2 font-medium text-right">Cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  {p.guias.map((g) => (
                    <tr key={g.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">
                        <Badge variant="info">{g.carrier}</Badge>
                      </td>
                      <td className="px-3 py-2 text-ink-700">{g.service}</td>
                      <td className="px-3 py-2 font-mono text-ink-700">{g.trackingNumber || '—'}</td>
                      <td className="px-3 py-2 text-ink-600">
                        {g.dims ? `${g.dims} cm` : '—'}
                        {g.weightKg != null && (
                          <span className="text-ink-400"> · {g.weightKg} kg</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-ink-700">
                        {g.costoRealCents != null ? centsToMXN(g.costoRealCents) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-ink-900">
                        {centsToMXN(g.cobradoCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
